import { css } from '@emotion/react'
import { Spacing, ZIndex } from '@opallabs/gem'

// The composer pane mirrors the Opal Document editor: fixed to the post
// shroud's content area between its header (65px) and footer (60px), to the
// right of the sidebar (480px, max 50%), and over the sidebar when expanded.
// The inset values and transitions are copied from
// Content/All/Document/styles.ts so the two types move identically.
const SHROUD_HEADER_HEIGHT = Spacing.XS * 16 + 1
const SHROUD_FOOTER_HEIGHT = Spacing.XS * 15
const SIDEBAR_INSET = `min(${Spacing.XL * 10}px, 50%)`

export const expandToggle = (isFullscreen: boolean) => css`
  position: fixed;
  left: ${isFullscreen ? '6px' : 'min(484px, 50%)'};
  top: calc(50vh - 65px);
  transition: left 0.3s ease-in-out;
  z-index: ${ZIndex.Shroud.Popover};
`

export const expandButton = css`
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.16);
  padding: 4px;
`

export const expandIcon = (isFullscreen: boolean) => css`
  transform: rotate(${isFullscreen ? '0deg' : '180deg'});
  transition: transform 0.3s ease-in-out;
`

export const pane = (isFullscreen: boolean) => css`
  position: fixed;
  inset-block-start: ${SHROUD_HEADER_HEIGHT}px;
  inset-block-end: ${SHROUD_FOOTER_HEIGHT}px;
  inset-inline-start: ${isFullscreen ? `${Spacing.XS * 3}px` : SIDEBAR_INSET};
  inset-inline-end: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  text-align: left;
  z-index: ${isFullscreen ? ZIndex.Element.Level5 : 0};
  transition:
    inset-inline-start 275ms ease-in-out,
    z-index 0s;
  transition-delay: ${isFullscreen ? '0s' : '0s, 275ms'};
`

export const paneHeader = css`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid #c3ccd9;
`

export const paneTitle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const paneBody = css`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f5f7fa;
`

export const panePreview = css`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
`

export const paneEmpty = css`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f5f7fa;
`

export const card = css`
  display: flex;
  flex-direction: column;
  width: 568px;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid #c3ccd9;
  border-radius: 10px;
  background: #ffffff;
`

export const preview = css`
  display: block;
  width: 100%;
  height: 426px;
  border: 0;
  background: #f5f7fa;
`

export const iconContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  background: #f5f7fa;
`

export const details = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px 16px;
`

export const fileName = css`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #161b24;
  overflow-wrap: anywhere;
`

export const fileMeta = css`
  margin: 0;
  font-size: 13px;
  color: #78859a;
`

export const actions = css`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`

export const openLink = css`
  font-size: 13px;
  font-weight: 600;
`

export const paste = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  width: 568px;
  max-width: 100%;
  padding: 24px;
  background: #ffffff;
  border: 1px dashed #c3ccd9;
  border-radius: 10px;
`

export const pasteLabel = css`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #161b24;
`

export const pasteInput = css`
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #c3ccd9;
  border-radius: 6px;
`

export const error = css`
  margin: 0;
  font-size: 13px;
  color: #b8473c;
`

export const placeholder = css`
  padding: 24px;
  font-size: 14px;
  color: #78859a;
`
